import type { ComponentType } from 'react';
import type { SpecialEntry, SpecialProps, Eligibility, PriceCompute } from './types';
import dynamic from 'next/dynamic';
import GenericSpecial from './GenericSpecial';
import RefundableBookingPGClassic from './RefundableBookingPGClassic';
import { refundablePercentOfCart } from './classic-pricing';

/**
 * [protect-dynamic] Widget de Protect Group que monta la pregunta `refundable_booking`:
 *  - `NEXT_PUBLIC_PROTECTGROUP_WIDGET=dynamic` → Pulse React Hybrid (`RefundableBookingPG`): Protect
 *    cotiza el precio y devuelve el `quoteId` con el que se registra la venta. Es el que recomienda
 *    Protect y el que se usa en desarrollo y QA.
 *  - cualquier otro valor o sin definir → el clásico (`RefundableBookingPGClassic`): % fijo de SQL y
 *    sin `quoteId`. Es el de producción mientras no tengamos las credenciales live de Protect, que
 *    el dinámico necesita para registrar la venta.
 * Se lee en cada llamada (no al cargar el módulo) para que los tests puedan fijarlo.
 */
export function isDynamicRefundableWidget(): boolean {
  return process.env.NEXT_PUBLIC_PROTECTGROUP_WIDGET === 'dynamic';
}

// Carga diferida: el SDK de Protect (`protect-group-pulse-react`) solo se descarga donde se monta el widget
// dinámico; en producción, con el clásico, no entra en el paquete del formulario.
export const RefundableBookingPGLazy = dynamic(() => import('./RefundableBookingPG'), { ssr: false });

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function readEmailFromForm(formValues: Record<string, unknown>): string {
  // Las preguntas estándar de email son questionTypeId = 6 con questionId variable.
  // Buscamos por convención el campo q_7 (email estándar más común) y, como fallback,
  // cualquier q_<n> cuyo valor encaje en regex de email.
  const candidate = formValues['q_7'];
  if (typeof candidate === 'string') return candidate.trim();
  for (const [key, val] of Object.entries(formValues)) {
    if (!key.startsWith('q_')) continue;
    if (typeof val !== 'string') continue;
    if (EMAIL_REGEX.test(val.trim())) return val.trim();
  }
  return '';
}

const requiresValidEmail: Eligibility = (_q, { formValues }) => {
  const email = readEmailFromForm(formValues);
  return EMAIL_REGEX.test(email);
};

const requiresProtectGroupConfig: Eligibility = (q, ctx) => {
  if (!process.env.NEXT_PUBLIC_PROTECTGROUP_VENDOR_CODE) return false;
  return requiresValidEmail(q, ctx);
};

/**
 * Registro de UI custom por internalAlias. Añadir aquí una entrada para cada empresa /
 * proveedor externo que requiera su propia pantalla. Si la pregunta no tiene
 * internalAlias o el alias no está registrado, se usa GenericSpecial como fallback.
 */
export const specialRegistry: Record<string, SpecialEntry> = {
  refundable_booking: {
    get Component() {
      return isDynamicRefundableWidget() ? RefundableBookingPGLazy : RefundableBookingPGClassic;
    },
    eligibility: requiresProtectGroupConfig,
    // Widget clásico (usp_Get_InsuranceProtectGroupScript_v3): SQL da un porcentaje y este es el precio.
    computePrice: refundablePercentOfCart,
    // Con el dinámico el precio es SOLO el que cotiza Protect: el porcentaje de SQL no se usa ni de respaldo.
    get requiresExternalPrice() {
      return isDynamicRefundableWidget();
    },
  },
};

export function getSpecialComponent(alias?: string): ComponentType<SpecialProps> {
  if (alias && specialRegistry[alias]) return specialRegistry[alias].Component;
  return GenericSpecial;
}

export function getSpecialEligibility(alias?: string): Eligibility | undefined {
  if (!alias) return undefined;
  return specialRegistry[alias]?.eligibility;
}

export function getSpecialRequiresExternalPrice(alias?: string): boolean {
  if (!alias) return false;
  return specialRegistry[alias]?.requiresExternalPrice === true;
}

export function getSpecialComputePrice(alias?: string): PriceCompute | undefined {
  if (!alias) return undefined;
  return specialRegistry[alias]?.computePrice;
}
